import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Removed Link as it's not used directly here
import { useAuth } from '../../contexts/AuthContext';

const EditQuotePage = () => {
  const { quoteId } = useParams(); // Changed from estimateId
  const navigate = useNavigate();
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    clientId: null,
    quoteNumber: '',         // Changed
    quoteDate: '',           // Changed
    expiryDate: '',          // Kept
    lineItems: [],
    subTotal: 0,
    taxRate: 0.0825,
    taxAmount: 0,
    totalAmount: 0,
    status: 'draft',
    notes: '',
    termsAndConditions: '',
    // Add any quote-specific fields here, e.g., serviceRequestId
    // serviceRequestId: null, 
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [originalQuoteNumber, setOriginalQuoteNumber] = useState('');

  useEffect(() => {
    const fetchClients = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/users?role=client', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch clients');
        const data = await response.json();
        if (data.status === 'success') {
          setClients(data.data.users || []);
        } else {
          throw new Error(data.message || 'Could not parse client data');
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        // setError('Failed to load clients.'); // Potentially set error
      }
    };
    fetchClients();
  }, [token]);

  useEffect(() => {
    if (!quoteId || !token) return;
    setIsLoading(true);
    fetch(`/api/quotes/${quoteId}`, { // Changed API endpoint
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(body => { 
            throw new Error(body.message || `Error ${res.status}`); 
          });
        }
        return res.json();
      })
      .then(payload => {
        // Assuming payload structure from backend for a single quote is { status: 'success', data: { quote: {...} } }
        // or just the quote object directly if your getQuoteByIdHandler returns that.
        // Adjust based on your actual API response. For now, assuming it's nested under data.quote.
        const quote = payload.data?.quote || payload.data || payload; // Adapt to actual structure

        if (!quote) {
            throw new Error('Quote data not found in response.');
        }

        setOriginalQuoteNumber(quote.quoteNumber);
        setFormData({
          id: quote.id,
          clientId: quote.client?.id || quote.clientId || null,
          quoteNumber: quote.quoteNumber || `QOT-${Date.now()}`,
          quoteDate: quote.quoteDate ? new Date(quote.quoteDate).toISOString().split('T')[0] : '',
          expiryDate: quote.expiryDate ? new Date(quote.expiryDate).toISOString().split('T')[0] : '',
          lineItems: quote.lineItems?.map(item => ({
            ...item,
            id: item.id, // Important for backend to identify existing line items
            total: parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0)
          })) || [],
          subTotal: parseFloat(quote.subTotal || 0),
          taxRate: parseFloat(quote.taxRate || 0.0825),
          taxAmount: parseFloat(quote.taxAmount || 0),
          totalAmount: parseFloat(quote.totalAmount || 0),
          status: quote.status || 'draft',
          notes: quote.notes || '',
          termsAndConditions: quote.termsAndConditions || '',
          // serviceRequestId: quote.serviceRequestId || null,
        });
      })
      .catch((err) => {
        console.error('Error fetching quote:', err);
        setError(`Failed to fetch quote: ${err.message}`);
      })
      .finally(() => setIsLoading(false));
  }, [quoteId, token]);

  useEffect(() => {
    let currentSubTotal = 0;
    formData.lineItems.forEach(item => {
      currentSubTotal += parseFloat(item.total || 0);
    });
    const currentTaxAmount = currentSubTotal * parseFloat(formData.taxRate || 0);
    const currentTotalAmount = currentSubTotal + currentTaxAmount;

    if (formData.subTotal !== currentSubTotal || formData.taxAmount !== currentTaxAmount || formData.totalAmount !== currentTotalAmount) {
        setFormData(prev => ({
            ...prev,
            subTotal: currentSubTotal,
            taxAmount: currentTaxAmount,
            totalAmount: currentTotalAmount,
        }));
    }
  }, [formData.lineItems, formData.taxRate]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value,
    }));
  };

  const handleLineItemChange = (index, e) => {
    const { name, value, type } = e.target;
    const updatedLineItems = [...formData.lineItems];
    const item = { ...updatedLineItems[index] };

    item[name] = type === 'number' ? parseFloat(value) : value;

    if (name === 'quantity' || name === 'unitPrice') {
      item.total = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
    }
    updatedLineItems[index] = item;
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unitPrice: 0, total: 0 }],
    }));
  };

  const removeLineItem = (index) => {
    const updatedLineItems = [...formData.lineItems];
    updatedLineItems.splice(index, 1);
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!formData.clientId) {
        setError('Please select a client.');
        setIsLoading(false);
        return;
    }
     if (formData.lineItems.some(item => !item.description || item.quantity <= 0 || item.unitPrice < 0)) {
        setError('Please ensure all line items have a description, positive quantity and non-negative unit price.');
        setIsLoading(false);
        return;
    }

    const payload = {
      ...formData,
      clientId: parseInt(formData.clientId, 10),
      quoteDate: new Date(formData.quoteDate).toISOString(), // Changed
      expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null,
      lineItems: formData.lineItems.map(item => ({
        id: item.id, // Keep ID for existing items
        description: item.description,
        quantity: parseFloat(item.quantity || 0),
        unitPrice: parseFloat(item.unitPrice || 0),
        total: parseFloat(item.total || 0), // Recalculated, but good to send
      })),
      taxRate: parseFloat(formData.taxRate || 0),
    };

    try {
      const response = await fetch(`/api/quotes/${quoteId}`, { // Changed API endpoint
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.message || `Error ${response.status}`);
      }
      alert('Quote updated successfully!');
      navigate(`/admin/quotes/view/${quoteId}`); // Navigate to view quote page
    } catch (err) {
      console.error('Error updating quote:', err);
      setError(err.message || 'An error occurred while updating the quote.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleConvertToEstimate = () => {
    navigate('/admin/estimates/new', { state: { quoteToConvert: formData } });
  };


  const inputClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const inputSmClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";

  if (isLoading && !clients.length) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading quote details...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <header className="mb-8">
            <h1 className="text-4xl font-bold text-brand-text-dark">Edit Quote {originalQuoteNumber ? `#${originalQuoteNumber}` : ''}</h1>
            <button onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:text-blue-800 mt-2">&larr; Back</button>
        </header>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }
  
  if (!formData.id && !isLoading) { 
    return (
        <div className="container mx-auto px-4 py-8 text-center">
            <p>Quote not found.</p>
            <button onClick={() => navigate("/admin/quotes")} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Go to Quotes
            </button>
        </div>
    );
}

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-brand-text-dark">Edit Quote {originalQuoteNumber ? `#${originalQuoteNumber}` : (formData.quoteNumber || '')}</h1>
        <button onClick={() => navigate(`/admin/quotes/view/${quoteId}`)} className="text-sm text-blue-600 hover:text-blue-800 mt-2">
          &larr; Back to View Quote
        </button>
      </header>

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 md:p-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong><span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">Client</label>
          <select name="clientId" id="clientId" value={formData.clientId || ''} onChange={handleChange} className={inputClass} required disabled={!clients.length}>
            <option value="" disabled>Select a client</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.username} ({client.email})</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label htmlFor="quoteNumber" className="block text-sm font-medium text-gray-700 mb-1">Quote Number</label>
            <input type="text" name="quoteNumber" id="quoteNumber" value={formData.quoteNumber} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="quoteDate" className="block text-sm font-medium text-gray-700 mb-1">Quote Date</label>
            <input type="date" name="quoteDate" id="quoteDate" value={formData.quoteDate} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
            <input type="date" name="expiryDate" id="expiryDate" value={formData.expiryDate} onChange={handleChange} className={inputClass} />
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select name="status" id="status" value={formData.status} onChange={handleChange} className={inputClass}>
            <option value="draft">Draft</option>
            <option value="sent_to_client">Sent to Client</option>
            <option value="accepted_by_client">Accepted by Client</option>
            <option value="rejected_by_client">Rejected by Client</option>
            <option value="converted_to_estimate">Converted to Estimate</option>
            <option value="converted_to_invoice">Converted to Invoice</option> {/* If direct conversion is possible */}
            <option value="expired">Expired</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Line Items Section */}
        <div className="mb-6 border-t pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
          {formData.lineItems.map((item, index) => (
            <div key={item.id || index} className="grid grid-cols-12 gap-x-3 gap-y-2 items-end p-3 mb-3 border rounded-md">
              <div className="col-span-12 sm:col-span-5">
                <label htmlFor={`itemDesc-${index}`} className="block text-xs font-medium text-gray-600">Description</label>
                <input type="text" name="description" id={`itemDesc-${index}`} value={item.description} onChange={(e) => handleLineItemChange(index, e)} className={inputSmClass} placeholder="Service or Item" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemQty-${index}`} className="block text-xs font-medium text-gray-600">Quantity</label>
                <input type="number" name="quantity" id={`itemQty-${index}`} value={item.quantity} onChange={(e) => handleLineItemChange(index, e)} className={`${inputSmClass} text-right`} min="0.01" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemPrice-${index}`} className="block text-xs font-medium text-gray-600">Unit Price</label>
                <input type="number" name="unitPrice" id={`itemPrice-${index}`} value={item.unitPrice} onChange={(e) => handleLineItemChange(index, e)} className={`${inputSmClass} text-right`} min="0.00" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemTotal-${index}`} className="block text-xs font-medium text-gray-600">Item Total</label>
                <input type="number" name="total" id={`itemTotal-${index}`} value={item.total.toFixed(2)} readOnly className={`${inputSmClass} text-right bg-gray-50`} />
              </div>
              <div className="col-span-12 sm:col-span-1 flex justify-end">
                {formData.lineItems.length > 1 && (
                  <button type="button" onClick={() => removeLineItem(index)} className="text-red-500 hover:text-red-700 p-1 rounded-md text-sm">Remove</button>
                )}
              </div>
            </div>
          ))}
          <button type="button" onClick={addLineItem} className="mt-2 text-blue-600 hover:text-blue-800 font-medium text-sm py-2 px-3 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors">
            + Add Line Item
          </button>
        </div>

        {/* Totals Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 border-t pt-6 mt-6">
          <div>
            <label htmlFor="subTotal" className="block text-sm font-medium text-gray-500">Subtotal</label>
            <input type="text" name="subTotal" id="subTotal" value={formData.subTotal.toFixed(2)} readOnly className={`${inputClass} bg-gray-50 text-right`} />
          </div>
          <div>
            <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700">Tax Rate</label>
            <input type="number" name="taxRate" id="taxRate" value={formData.taxRate} onChange={handleChange} className={`${inputClass} text-right`} step="0.0001" />
          </div>
          <div>
            <label htmlFor="taxAmount" className="block text-sm font-medium text-gray-500">Tax Amount</label>
            <input type="text" name="taxAmount" id="taxAmount" value={formData.taxAmount.toFixed(2)} readOnly className={`${inputClass} bg-gray-50 text-right`} />
          </div>
        </div>
        <div className="text-right mb-6 border-t pt-4">
          <p className="text-sm text-gray-500">Total Amount</p>
          <p className="text-2xl font-bold text-gray-800">${formData.totalAmount.toFixed(2)}</p>
        </div>

        {/* Notes and Terms */}
        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea name="notes" id="notes" value={formData.notes} onChange={handleChange} rows="3" className={inputClass} placeholder="Internal notes or notes for the client..."></textarea>
        </div>
        <div className="mb-6">
          <label htmlFor="termsAndConditions" className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions (Optional)</label>
          <textarea name="termsAndConditions" id="termsAndConditions" value={formData.termsAndConditions} onChange={handleChange} rows="3" className={inputClass} placeholder="Define validity, scope, etc..."></textarea>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate(`/admin/quotes/view/${quoteId}`)}
            className="mr-3 px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="mr-3 px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
          {/* Add Convert to Estimate button if quote is in a convertible status */}
          {(formData.status === 'accepted_by_client' || formData.status === 'sent_to_client' || formData.status === 'draft') && formData.id && (
            <button
              type="button"
              onClick={handleConvertToEstimate}
              className="ml-3 px-6 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={isLoading}
            >
              Convert to Estimate
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default EditQuotePage; 